import {
  type UpdateLlmPolicy,
  fetchLlmModels,
  fetchLlmPolicy,
  updateLlmPolicy,
} from "@app/deploy";
import { useDispatch, useLoader, useLoaderSuccess, useQuery } from "@app/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { BannerMessages } from "../banner";
import { Box } from "../box";
import { Button, ButtonAdmin } from "../button";
import { FormGroup } from "../form-group";
import { Group } from "../group";
import { IconX } from "../icons";
import { MultiSelect } from "../multi-select";
import type { SelectOption } from "../select";
import { tokens } from "../tokens";

const PROVIDER_DISPLAY_PRETTY_NAMES: Record<string, string> = {
  openai: "OpenAI",
  google: "Google Gemini",
};

function getProviderDisplayName(provider: string): string {
  return (
    PROVIDER_DISPLAY_PRETTY_NAMES[provider] ??
    provider.charAt(0).toUpperCase() + provider.slice(1)
  );
}

function getProviderFromModelId(id: string): string {
  const idx = id.indexOf("/");
  return idx === -1 ? "" : id.slice(0, idx);
}

function getModelShortLabel(id: string): string {
  const idx = id.indexOf("/");
  return idx === -1 ? id : id.slice(idx + 1);
}

export const LlmPolicyEditor = ({
  envId,
  orgId,
  initialSpendLimit,
  initialModels,
  onClose,
}: {
  envId: string;
  orgId: string;
  initialSpendLimit: number | null;
  initialModels: string[];
  onClose: () => void;
}) => {
  const dispatch = useDispatch();
  useQuery(fetchLlmModels());
  const modelsLoader = useLoader(fetchLlmModels);
  const allModels: { id: string; supported: boolean }[] =
    modelsLoader.meta?.models ?? [];

  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const hasAppliedInitial = useRef(false);

  useEffect(() => {
    if (allModels.length === 0) return;
    const allowedIds = new Set(allModels.map((m) => m.id));
    const providers = new Set(
      allModels
        .map((m) => getProviderFromModelId(m.id))
        .filter((p): p is string => Boolean(p)),
    );
    for (const p of providers) allowedIds.add(`${p}/*`);
    if (!hasAppliedInitial.current) {
      hasAppliedInitial.current = true;
      setSelectedModels(initialModels.filter((id) => allowedIds.has(id)));
    } else {
      setSelectedModels((prev) => prev.filter((id) => allowedIds.has(id)));
    }
  }, [allModels, initialModels]);

  const modelsByProvider = useMemo(() => {
    const map = new Map<string, { id: string; supported: boolean }[]>();
    for (const m of allModels) {
      const provider = getProviderFromModelId(m.id);
      if (!provider) continue;
      const list = map.get(provider) ?? [];
      list.push(m);
      map.set(provider, list);
    }
    const sorted = Array.from(map.entries()).sort(([a], [b]) =>
      a.localeCompare(b),
    );
    return sorted;
  }, [allModels]);

  const optionsByProvider = useMemo(() => {
    const map = new Map<string, SelectOption<string>[]>();
    for (const [provider, models] of modelsByProvider) {
      const modelOptions = models.map((m) => ({
        value: m.id,
        label: getModelShortLabel(m.id),
      }));
      map.set(provider, [
        { value: `${provider}/*`, label: "*" },
        ...modelOptions,
      ]);
    }
    return map;
  }, [modelsByProvider]);

  const data: UpdateLlmPolicy = {
    envId,
    orgId,
    spendLimit: initialSpendLimit,
    models: selectedModels,
  };
  const updateLoader = useLoader(updateLlmPolicy);

  useLoaderSuccess(updateLoader, () => {
    dispatch(fetchLlmPolicy({ envId, orgId }));
    onClose();
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    dispatch(updateLlmPolicy(data));
  };

  const setSelectedForProvider = (provider: string) => (values: string[]) => {
    setSelectedModels((prev) => [
      ...prev.filter((id) => getProviderFromModelId(id) !== provider),
      ...values,
    ]);
  };

  return (
    <Box>
      <Group>
        <h3 className={tokens.type.h3}>LLM Usage & Access Policy</h3>
        <BannerMessages {...updateLoader} />
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="flex flex-col gap-4">
              <h4 className="text-lg font-semibold text-gray-900">
                Select Allowed Models
              </h4>
              {modelsLoader.isLoading ? (
                <p className="text-sm text-gray-500">Loading models...</p>
              ) : modelsByProvider.length === 0 ? (
                <p className="text-sm text-gray-500">No models available.</p>
              ) : (
                modelsByProvider.map(([provider]) => {
                  const options = optionsByProvider.get(provider) ?? [];
                  const selectedForProvider = selectedModels.filter(
                    (id) => getProviderFromModelId(id) === provider,
                  );
                  return (
                    <FormGroup
                      key={provider}
                      label={getProviderDisplayName(provider)}
                      htmlFor={`models-${provider}`}
                      labelProps={{
                        className: "text-base font-semibold text-gray-900",
                      }}
                    >
                      <MultiSelect<string>
                        id={`models-${provider}`}
                        ariaLabel={`Select ${getProviderDisplayName(provider)} models`}
                        options={options}
                        value={selectedForProvider}
                        onChange={(values) =>
                          setSelectedForProvider(provider)(values)
                        }
                        placeholder="None"
                        getTriggerDisplayText={(selected) =>
                          selected.length === 0
                            ? "None"
                            : selected
                                .map(
                                  (v) =>
                                    options.find((o) => o.value === v)?.label ??
                                    getModelShortLabel(v),
                                )
                                .join(", ")
                        }
                        showSelectAllButton={true}
                        showAllButton={true}
                      />
                    </FormGroup>
                  );
                })
              )}
            </div>

            <div className="pt-0 lg:pt-7">
              <div className="flex flex-col gap-4 rounded-md border border-gray-200 p-4">
                <h4 className="text-lg font-semibold text-gray-900">
                  Selected
                </h4>
                <div className="flex max-h-48 flex-wrap gap-2 overflow-y-auto min-h-[2rem]">
                  {selectedModels.map((id) => (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-gray-50 px-2 py-1 text-sm"
                    >
                      <span>{id}</span>
                      <button
                        type="button"
                        aria-label={`Remove ${id}`}
                        className="text-gray-500 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 rounded"
                        onClick={() =>
                          setSelectedModels((prev) =>
                            prev.filter((x) => x !== id),
                          )
                        }
                      >
                        <IconX variant="sm" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <hr className="my-2" />
          <div className="flex gap-2">
            <ButtonAdmin
              type="submit"
              envId={envId}
              isLoading={updateLoader.isLoading}
            >
              Save Policy
            </ButtonAdmin>
            <Button type="button" onClick={onClose} variant="white">
              Cancel
            </Button>
          </div>
        </form>
      </Group>
    </Box>
  );
};
